import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import EditIcon from '@material-ui/icons/Edit';
import { Button as MIButton } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import { ErrorInfo, isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import { trackPage } from '../../../shared/services/utils/tracking/tracking.service';
import {
  getAllAmenities,
  getAllChannelAmenities,
  getAllChannelRoomAmenities
} from '../../../shared/services/data/lookups.data';
import { getModuleName } from '../../shared/services/amenity.service';
import { AmenityUiModel } from '../../../shared/ui-models/amenity.ui-model';
import {
  addNewAmenity,
  deleteAmenity,
  updateAmenity,
  uploadAmenityImage
} from '../../shared/services/data.service';
import { DeleteAmenityModal } from './components/delete-amenity-modal/delete-amenity-modal.component';
import { EditAmenityModal } from './components/edit-amenity-modal/edit-amenity-modal.component';
import { AddAmenityModal } from './components/add-amenity-modal/add-amenity-modal.component';
import useWindowResizer from '../../../shared/hooks/window-resizer/window-resizer.hook';
import { PUBLIC_PATH } from '../../../shared/config/routes-config';
import { ImageType } from 'react-images-uploading';
import { AmenityChannelUiModel } from '../../../shared/ui-models/amenity-channel.ui-model';
import Button, { ButtonType } from '../../../shared/components/core-ui/button/button.component';
import { birdToast } from '../../../shared/components/core-ui/toast';
export const AmenitiesListContainer = () => {
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [amenitiesList, setAmenitiesList] = useState<AmenityUiModel[]>([]);
  const [amenitiesChannelList, setAmenitiesChannelList] = useState<AmenityChannelUiModel[]>([]);
  const [amenitiesChannelRoomList, setAmenitiesChannelRoomList] = useState<AmenityChannelUiModel[]>(
    []
  );
  const [amenityToUpdate, setAmenityUpdate] = useState<AmenityUiModel>();
  const [amenityToDelete, setAmenityDelete] = useState<AmenityUiModel>();
  const { t } = useTranslation();
  const isMobileView = useWindowResizer();

  const handleNewAmenitySubmit = (newAmenityName: string) => {
    const promise = addNewAmenity({ name: newAmenityName }).then(
      (response: AmenityUiModel | ErrorInfo) => {
        if (isErrorInfo(response)) {
          throw response;
        } else {
          setAmenitiesList([...amenitiesList, response]);
        }
      }
    );

    birdToast.promise(promise, {
      loadingMessage: 'Adding amenity...',
      successMessage: t('AMENITY_MODULE.LIST_CONTAINER.NOTIFICATIONS.ADD_AMENITY_SUCCESS.MESSAGE'),
      errorPermissionMessage: t(
        'AMENITY_MODULE.LIST_CONTAINER.NOTIFICATIONS.ADD_AMENITY_PERMISSION.MESSAGE'
      ),
      errorFailedMessage: t(
        'AMENITY_MODULE.LIST_CONTAINER.NOTIFICATIONS.ADD_AMENITY_FAILED.MESSAGE'
      )
    });
  };

  const uploadImageHandler = (amenityId: string, image: ImageType) => {
    if (!(image && image.file)) return;

    const promise = uploadAmenityImage(image.file, amenityId).then(
      (response: AmenityUiModel | ErrorInfo) => {
        if (!isErrorInfo(response)) {
          const updateAmenitiesList = amenitiesList.map((amenity: AmenityUiModel) => {
            if (amenity.id === response.id) {
              return response;
            }
            return amenity;
          });
          setAmenitiesList(updateAmenitiesList);
          return;
        }
        throw response;
      }
    );
    birdToast.promise(promise, {
      loadingMessage: 'Updating amenity...',
      successMessage: t('AMENITY_MODULE.LIST_CONTAINER.NOTIFICATIONS.EDIT_AMENITY_SUCCESS.MESSAGE'),
      errorPermissionMessage: t(
        'AMENITY_MODULE.LIST_CONTAINER.NOTIFICATIONS.EDIT_AMENITY_PERMISSION.MESSAGE'
      ),
      errorFailedMessage: t(
        'AMENITY_MODULE.LIST_CONTAINER.NOTIFICATIONS.EDIT_AMENITY_FAILED.MESSAGE'
      )
    });
  };

  const handleAmenityEdit = (amenityId: string, amenity: AmenityUiModel) => {
    const promise = updateAmenity(amenityId, amenity).then(
      (response: AmenityUiModel | ErrorInfo) => {
        if (isErrorInfo(response)) {
          throw response;
        } else {
          const updateAmenitiesList = amenitiesList.map((amenity: AmenityUiModel) => {
            if (amenity.id === response.id) {
              return response;
            } else {
              return amenity;
            }
          });
          setAmenitiesList(updateAmenitiesList);
        }
      }
    );

    birdToast.promise(promise, {
      loadingMessage: 'Updating amenity...',
      successMessage: t('AMENITY_MODULE.LIST_CONTAINER.NOTIFICATIONS.EDIT_AMENITY_SUCCESS.MESSAGE'),
      errorPermissionMessage: t(
        'AMENITY_MODULE.LIST_CONTAINER.NOTIFICATIONS.EDIT_AMENITY_PERMISSION.MESSAGE'
      ),
      errorFailedMessage: t(
        'AMENITY_MODULE.LIST_CONTAINER.NOTIFICATIONS.EDIT_AMENITY_PERMISSION.MESSAGE'
      )
    });
  };

  const handleAmenityDelete = (amenityId: string) => {
    const promise = deleteAmenity(amenityId).then((response: any | ErrorInfo) => {
      if (isErrorInfo(response)) {
        throw response;
      } else {
        const updateAmenitiesList = amenitiesList.filter(
          (amenity: AmenityUiModel) => amenity.id !== amenityId
        );
        setAmenitiesList(updateAmenitiesList);
      }
    });

    birdToast.promise(promise, {
      loadingMessage: 'Deleting amenity...',
      successMessage: t(
        'AMENITY_MODULE.LIST_CONTAINER.NOTIFICATIONS.DELETE_AMENITY_SUCCESS.MESSAGE'
      ),
      errorPermissionMessage: t(
        'AMENITY_MODULE.LIST_CONTAINER.NOTIFICATIONS.DELETE_AMENITY_PERMISSION.MESSAGE'
      ),
      errorFailedMessage: t(
        'AMENITY_MODULE.LIST_CONTAINER.NOTIFICATIONS.DELETE_AMENITY_FAILED.MESSAGE'
      )
    });
  };

  useEffect(() => {
    trackPage('amenities-list');
    getAllAmenities(getModuleName()).then((response: AmenityUiModel[] | ErrorInfo) => {
      if (!isErrorInfo(response)) {
        setAmenitiesList(response);
        getAllChannelAmenities(getModuleName()).then(
          (response: AmenityChannelUiModel[] | ErrorInfo) => {
            if (!isErrorInfo(response)) {
              setAmenitiesChannelList(response);
            }
          }
        );
        getAllChannelRoomAmenities(getModuleName()).then(
          (response: AmenityChannelUiModel[] | ErrorInfo) => {
            if (!isErrorInfo(response)) {
              setAmenitiesChannelRoomList(response);
            }
          }
        );
      }
    });
  }, []);

  return (
    <div className="flex h-full flex-col items-start justify-start md:min-h-[calc(100vh-45px)] md:bg-[#f5f7fc] md:px-[2rem] md:py-[3rem]">
      <div className="flex w-full items-center justify-between gap-[1rem]">
        <div className="my-[1rem] text-[2.5rem] font-bold text-primary-500">
          {t('AMENITY_MODULE.LIST_CONTAINER.TITLE')}
        </div>
        <Button
          type={ButtonType.primary}
          onClick={() => {
            setShowAddModal(true);
          }}
        >
          Add Amenity
        </Button>
      </div>
      <div className="grid h-full min-h-[400px] w-full items-stretch gap-[1.2rem] pb-[2rem] [grid-template-columns:1fr] md:[grid-template-columns:repeat(auto-fill,minmax(320px,1fr))] md:gap-[1.6rem]">
        {amenitiesList.map((amenity, index) => (
          <div
            key={amenity.id ? amenity.id : `amenity-${index}`}
            className="flex h-full w-full flex-col items-center rounded-[4px] bg-white px-[1.4rem] py-[1.5rem] shadow-[0_2px_4px_0_rgba(187,186,186,0.5)] transition-[box-shadow,transform] duration-150 ease-in-out hover:-translate-y-[1px] hover:shadow-[0_6px_16px_rgba(187,186,186,0.55)] md:p-[1.4rem]"
          >
            <div className="mb-[1rem] flex h-[150px] w-full flex-none items-center justify-center">
              <img
                className="h-full w-full rounded-[8px] object-cover"
                alt={amenity.name || t('AMENITY_MODULE.LIST_CONTAINER.UNTITLED_AMENITY')}
                src={amenity?.image || `${PUBLIC_PATH}/assets/pngs/amenity-default.png`}
              />
            </div>
            <div className="mb-[2rem] flex w-full flex-row items-center md:mb-[0.6rem]">
              <div
                className={`flex-1 overflow-hidden text-left text-[1.8rem] font-bold ${
                  !amenity.name ? 'text-[#d3d3d3]' : 'text-[#37454d]'
                }`}
              >
                {amenity.name || t('AMENITY_MODULE.LIST_CONTAINER.UNTITLED_AMENITY')}
                <p className="mt-0 text-[1rem] leading-[1.3] text-gray-500">
                  {t('AMENITY_MODULE.LIST_CONTAINER.MODIFIED_BY')}
                  <span className="ml-[0.4rem] text-[1.2rem] font-bold">
                    {amenity.modifiedByUsername}
                  </span>
                </p>
                <div className="flex w-full items-center justify-between gap-[0.6rem]">
                  <p className="m-0 text-[1rem] leading-[1.3] text-gray-500">
                    {t('AMENITY_MODULE.LIST_CONTAINER.MODIFIED_AT')}
                    <span className="ml-[0.4rem] text-[1.2rem] font-bold">
                      {amenity.modifiedAt}
                    </span>
                  </p>

                  {isMobileView && (
                    <div className="flex items-center justify-end gap-[0.2rem] [&_button]:min-w-0 [&_button]:p-[0.6rem] [&_svg]:text-[2.2rem]">
                      <MIButton
                        aria-label={t('AMENITY_MODULE.LIST_CONTAINER.EDIT_LINK')}
                        onClick={() => {
                          setAmenityUpdate(amenity);
                          setShowEditModal(true);
                        }}
                      >
                        <EditIcon fontSize={'default'} color="inherit" />
                      </MIButton>
                      <MIButton
                        aria-label={t('AMENITY_MODULE.LIST_CONTAINER.DELETE_LINK')}
                        onClick={() => {
                          setAmenityDelete(amenity);
                          setShowDeleteModal(true);
                        }}
                      >
                        <DeleteIcon fontSize="default" color={'error'} />
                      </MIButton>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {!isMobileView && (
              <div className="mt-auto flex w-full items-center justify-end gap-[0.4rem] [&_button]:min-w-0">
                <MIButton
                  aria-controls="amenity-menu"
                  aria-haspopup="true"
                  onClick={() => {
                    setAmenityUpdate(amenity);
                    setShowEditModal(true);
                  }}
                >
                  <EditIcon fontSize={'large'} color="inherit" />
                </MIButton>
                <MIButton
                  aria-controls="amenity-menu"
                  aria-haspopup="true"
                  onClick={() => {
                    setAmenityDelete(amenity);
                    setShowDeleteModal(true);
                  }}
                >
                  <DeleteIcon fontSize="large" color={'error'} />
                </MIButton>
              </div>
            )}
          </div>
        ))}
        {/* <DataGrid apiRef={apiRef} className={classes.root} rows={amenitiesList} columns={columns} /> */}
      </div>
      <AddAmenityModal
        config={{ showModal: showAddModal }}
        eventHandlers={{
          cancelHandler: () => {
            setShowAddModal(false);
          },
          addAmenityHandler: (amenityName) => {
            setShowAddModal(false);
            handleNewAmenitySubmit(amenityName);
          }
        }}
      />
      <EditAmenityModal
        config={{ showModal: showEditModal }}
        data={{
          amenity: amenityToUpdate,
          cAmenityList: amenitiesChannelList,
          cAmenityRoomList: amenitiesChannelRoomList
        }}
        eventHandlers={{
          cancelHandler: () => {
            setShowEditModal(false);
            setAmenityUpdate(undefined);
          },
          editAmenityHandler: (amenityId: string, amenity: AmenityUiModel) => {
            setShowEditModal(false);
            setAmenityUpdate(undefined);
            handleAmenityEdit(amenityId, amenity);
          },
          submitImageHandler: (id: string, file: ImageType) => {
            setShowEditModal(false);
            setAmenityUpdate(undefined);
            uploadImageHandler(id, file);
          }
        }}
      />
      <DeleteAmenityModal
        config={{ showModal: showDeleteModal }}
        data={{ amenity: amenityToDelete }}
        eventHandlers={{
          cancelHandler: () => {
            setShowDeleteModal(false);
            setAmenityDelete(undefined);
          },
          deleteAmenityHandler: (amenity: AmenityUiModel) => {
            setShowDeleteModal(false);
            setAmenityDelete(undefined);
            if (amenity && amenity.id) {
              handleAmenityDelete(amenity.id);
            }
          }
        }}
      />
    </div>
  );
};

export default AmenitiesListContainer;
